import React, { useState, useEffect, useCallback } from 'react';
import { Building2, DollarSign, RefreshCw, CheckCircle, AlertCircle, Info, Package, TrendingDown, ChevronDown, ChevronRight, Truck, Trash2, Plus } from 'lucide-react';
import PageIdentifier from '../shared/PageIdentifier';
import SCREEN_IDS from '../../utils/screenIds';
import usePermissions from '../../hooks/usePermissions';

const fmt = (n, decimals = 2) => parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
const fmtINR = (n) => parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const VendorLedger = () => {
  const { hasPermission } = usePermissions();
  const canDelete = hasPermission('vendor-ledger', 'delete');
  const [vendors, setVendors] = useState([]);
  const [selectedVendorId, setSelectedVendorId] = useState('');
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Payment form
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentType, setPaymentType] = useState('cash');
  const [amountUSD, setAmountUSD] = useState('');
  const [goldGrams, setGoldGrams] = useState('');
  const [goldRatePerGram, setGoldRatePerGram] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [paymentMsg, setPaymentMsg] = useState('');
  const [paymentErr, setPaymentErr] = useState('');

  const [expandedRows, setExpandedRows] = useState(() => new Set());
  const [ledgerTableOpen, setLedgerTableOpen] = useState(true);
  const toggleRow = (idx) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };
  const [showChargeForm, setShowChargeForm] = useState(null); // groupKey of open form
  const [chargeType, setChargeType] = useState('Transport');
  const [chargeDesc, setChargeDesc] = useState('');
  const [chargeAmtINR, setChargeAmtINR] = useState('');
  const [chargeAmtUSD, setChargeAmtUSD] = useState('');
  const [chargeSubmitting, setChargeSubmitting] = useState(false);
  const [chargeMsg, setChargeMsg] = useState('');

  const CHARGE_TYPES = ['Transport', 'Customs', 'Handling', 'Insurance', 'Brokerage', 'Other'];

  const handleAddCharge = async (arrival) => {
    if (!chargeAmtINR && !chargeAmtUSD) { setChargeMsg('Enter INR or USD amount'); return; }
    setChargeSubmitting(true);
    setChargeMsg('');
    try {
      const body = {
        arrival_date: arrival.date,
        charge_type: chargeType,
        description: chargeDesc,
        amount_inr: chargeAmtINR || null,
        amount_usd: chargeAmtUSD || null,
        upload_batch_id: arrival.upload_batch_id || null,
        vendor_import_batch_id: arrival.vendor_import_batch_id || null,
      };
      const res = await fetch(`/api/vendors/${selectedVendorId}/shipment-charges`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        setChargeAmtINR(''); setChargeAmtUSD(''); setChargeDesc('');
        setShowChargeForm(null);
        await loadLedger(selectedVendorId);
      } else {
        setChargeMsg(data.error || 'Failed to add charge');
      }
    } catch (e) {
      setChargeMsg('Network error: ' + e.message);
    } finally {
      setChargeSubmitting(false);
    }
  };

  const handleDeleteCharge = async (chargeId) => {
    if (!canDelete) return;
    if (!window.confirm('Remove this charge?')) return;
    try {
      const res = await fetch(`/api/vendors/${selectedVendorId}/shipment-charges/${chargeId}`, {
        method: 'DELETE', credentials: 'include',
      });
      const data = await res.json();
      if (data.success) await loadLedger(selectedVendorId);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    fetch('/api/vendors', { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) setVendors(data);
        else if (data.vendors) setVendors(data.vendors);
      })
      .catch(() => {});
  }, []);

  const loadLedger = useCallback(async (vendorId) => {
    if (!vendorId) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/vendors/${vendorId}/ledger`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setLedger(data);
      else setError(data.error || 'Failed to load ledger');
    } catch (e) {
      setError('Network error: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleVendorChange = (id) => {
    setSelectedVendorId(id);
    setLedger(null);
    setPaymentMsg('');
    setPaymentErr('');
    setShowPaymentForm(false);
    setExpandedRows(new Set());
    if (id) loadLedger(id);
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setPaymentMsg('');
    setPaymentErr('');

    const body = paymentType === 'cash'
      ? { payment_type: 'cash', amount_usd: amountUSD, payment_method: paymentMethod, reference_number: referenceNumber, notes: paymentNotes }
      : { payment_type: 'gold', gold_grams: goldGrams, gold_rate_per_gram: goldRatePerGram, notes: paymentNotes };

    try {
      const res = await fetch(`/api/vendors/${selectedVendorId}/payments`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        setPaymentMsg(paymentType === 'cash'
          ? `Cash payment of $${fmt(amountUSD)} recorded.`
          : `Gold exchange of ${parseFloat(goldGrams).toFixed(3)}g recorded.`
        );
        setAmountUSD(''); setGoldGrams(''); setGoldRatePerGram('');
        setReferenceNumber(''); setPaymentNotes('');
        setShowPaymentForm(false);
        await loadLedger(selectedVendorId);
        setTimeout(() => setPaymentMsg(''), 6000);
      } else {
        setPaymentErr(data.error || 'Failed to record payment');
      }
    } catch (e) {
      setPaymentErr('Network error: ' + e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const goldINRPreview = goldGrams && goldRatePerGram
    ? parseFloat(goldGrams) * parseFloat(goldRatePerGram)
    : null;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <PageIdentifier screenId={SCREEN_IDS.VENDORS.VENDOR_LEDGER} />

      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Building2 className="text-amber-600" size={28} />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Vendor Ledger</h1>
            <p className="text-sm text-gray-500">Chronological account of items received and payments made — values locked at entry-date rates</p>
          </div>
        </div>

        {/* Vendor Selector */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-2">Select Vendor</label>
          <select
            value={selectedVendorId}
            onChange={e => handleVendorChange(e.target.value)}
            className="w-full max-w-sm border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
          >
            <option value="">— Choose a vendor —</option>
            {vendors.map(v => (
              <option key={v.id} value={v.id}>{v.name}{v.company ? ` (${v.company})` : ''}</option>
            ))}
          </select>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <RefreshCw size={24} className="animate-spin mr-2" /> Loading ledger…
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2 text-red-700 text-sm mb-6">
            <AlertCircle size={16} /> {error}
          </div>
        )}

        {ledger && !loading && ledger.ledger_entries && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Items Value</div>
                <div className="text-2xl font-bold text-gray-800">${fmt(ledger.summary.total_usd_owed)}</div>
                <div className="text-xs text-gray-400 mt-1">{ledger.summary.item_count} items — entry-date rates</div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Shipment Charges</div>
                <div className="text-2xl font-bold text-orange-600">${fmt(ledger.summary.total_charges_usd || 0)}</div>
                <div className="text-xs text-gray-400 mt-1">Transport, customs, etc. — not owed to vendor</div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Total Paid</div>
                <div className="text-2xl font-bold text-green-700">${fmt(ledger.summary.total_paid_usd)}</div>
                <div className="text-xs text-gray-400 mt-1">{ledger.summary.payment_count} payment{ledger.summary.payment_count !== 1 ? 's' : ''} — cash + gold</div>
              </div>
              <div className={`rounded-xl border p-5 ${
                ledger.summary.outstanding_usd > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
              }`}>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Outstanding</div>
                <div className={`text-2xl font-bold ${ledger.summary.outstanding_usd > 0 ? 'text-red-700' : 'text-green-700'}`}>
                  ${fmt(ledger.summary.outstanding_usd)}
                </div>
                <div className="text-xs text-gray-500 mt-1">Items − paid (excludes shipment charges)</div>
              </div>
            </div>

            {/* Rate note */}
            <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-6 text-xs text-blue-700">
              <Info size={14} className="mt-0.5 flex-shrink-0" />
              <span>
                Each item's USD value = (Gold 24K grams × gold rate on entry date + stone/making/other costs) ÷ dollar rate on entry date.
                Gold exchanges are converted using the rate recorded at time of payment. Balances are <strong>fixed</strong> — not affected by today's prices.
              </span>
            </div>

            {/* Success / error messages */}
            {paymentMsg && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-sm text-green-800">
                <CheckCircle size={14} /> {paymentMsg}
              </div>
            )}
            {paymentErr && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-sm text-red-800">
                <AlertCircle size={14} /> {paymentErr}
              </div>
            )}

            {/* Ledger Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-6">
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <button
                  onClick={() => setLedgerTableOpen(o => !o)}
                  className="flex items-center gap-2 font-bold text-gray-800 hover:text-amber-700 transition"
                >
                  {ledgerTableOpen
                    ? <ChevronDown size={16} className="text-gray-400" />
                    : <ChevronRight size={16} className="text-gray-400" />
                  }
                  Account Ledger — {ledger.vendor.code || ledger.vendor.name}
                  <span className="text-xs font-normal text-gray-400">({ledger.ledger_entries.length} entries)</span>
                </button>
                <button
                  onClick={() => { setShowPaymentForm(f => !f); setPaymentErr(''); }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-lg transition"
                >
                  <DollarSign size={14} />
                  {showPaymentForm ? 'Cancel' : 'Record Payment'}
                </button>
              </div>

              {/* Inline Payment Form */}
              {showPaymentForm && (
                <div className="border-b border-gray-100 bg-gray-50 px-5 py-4">
                  <form onSubmit={handleRecordPayment} className="space-y-4">
                    <div className="flex gap-2 mb-2">
                      {['cash', 'gold'].map(t => (
                        <button key={t} type="button"
                          onClick={() => setPaymentType(t)}
                          className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition ${
                            paymentType === t
                              ? t === 'cash' ? 'bg-blue-600 text-white' : 'bg-yellow-500 text-white'
                              : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-100'
                          }`}>
                          {t === 'cash' ? '$ Cash' : 'Gold Exchange'}
                        </button>
                      ))}
                    </div>

                    {paymentType === 'cash' ? (
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Amount USD *</label>
                          <input type="number" step="0.01" min="0.01" value={amountUSD}
                            onChange={e => setAmountUSD(e.target.value)} required
                            placeholder="0.00"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Method</label>
                          <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
                            <option value="bank_transfer">Bank Transfer</option>
                            <option value="cash">Cash</option>
                            <option value="check">Check</option>
                            <option value="wire">Wire</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Reference #</label>
                          <input type="text" value={referenceNumber}
                            onChange={e => setReferenceNumber(e.target.value)}
                            placeholder="Txn / check number"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Notes</label>
                          <input type="text" value={paymentNotes} onChange={e => setPaymentNotes(e.target.value)}
                            placeholder="Optional"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Gold Grams *</label>
                          <input type="number" step="0.001" min="0.001" value={goldGrams}
                            onChange={e => setGoldGrams(e.target.value)} required
                            placeholder="e.g. 25.500"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Gold Rate ₹/g *</label>
                          <input type="number" step="0.01" min="0.01" value={goldRatePerGram}
                            onChange={e => setGoldRatePerGram(e.target.value)} required
                            placeholder="e.g. 9500"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Notes</label>
                          <input type="text" value={paymentNotes} onChange={e => setPaymentNotes(e.target.value)}
                            placeholder="Optional"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
                        </div>
                        <div className="flex items-end">
                          {goldINRPreview !== null && (
                            <div className="w-full text-sm font-semibold text-yellow-800 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2">
                              ≈ ₹{fmtINR(goldINRPreview)} INR
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="flex gap-2">
                      <button type="submit" disabled={submitting}
                        className={`inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-bold text-white disabled:opacity-50 transition ${
                          paymentType === 'cash' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-yellow-500 hover:bg-yellow-600'
                        }`}>
                        {submitting && <RefreshCw size={13} className="animate-spin" />}
                        {submitting ? 'Saving…' : 'Save Payment'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Ledger Rows */}
              {ledgerTableOpen && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="w-8"></th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600 w-32">Date</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Description</th>
                      <th className="text-right px-4 py-3 font-semibold text-red-600">Debit (USD)</th>
                      <th className="text-right px-4 py-3 font-semibold text-green-600">Credit (USD)</th>
                      <th className="text-right px-4 py-3 font-semibold text-gray-700">Balance (USD)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.ledger_entries.length === 0 ? (
                      <tr><td colSpan={6} className="text-center py-12 text-gray-400">No entries yet for this vendor</td></tr>
                    ) : ledger.ledger_entries.map((entry, idx) => {
                      const isRowOpen = expandedRows.has(idx);
                      const hasDetails = !!entry.details || entry.entry_type === 'arrival' || entry.entry_type === 'charge';
                      return (
                        <React.Fragment key={idx}>
                          <tr
                            onClick={() => hasDetails && toggleRow(idx)}
                            className={`border-t border-gray-50 hover:bg-gray-50 ${hasDetails ? 'cursor-pointer' : ''} ${
                              entry.entry_type === 'payment' ? 'bg-green-50/40' :
                              entry.entry_type === 'charge'  ? 'bg-orange-50/40' : ''
                            }`}
                          >
                            <td className="pl-3">
                              {hasDetails && (
                                isRowOpen
                                  ? <ChevronDown size={13} className="text-gray-400" />
                                  : <ChevronRight size={13} className="text-gray-400" />
                              )}
                            </td>
                            <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{fmtDate(entry.date)}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                {entry.entry_type === 'arrival'
                                  ? <Package size={13} className="text-red-400 flex-shrink-0" />
                                  : entry.entry_type === 'charge'
                                  ? <Truck size={13} className="text-orange-500 flex-shrink-0" />
                                  : <TrendingDown size={13} className="text-green-500 flex-shrink-0" />
                                }
                                <div className={`font-semibold ${
                                  entry.entry_type === 'arrival' ? 'text-gray-800' :
                                  entry.entry_type === 'charge' ? 'text-orange-800' :
                                  'text-green-800'
                                }`}>
                                  {entry.description}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-red-600">
                              {entry.debit_usd > 0 ? `$${fmt(entry.debit_usd)}` : ''}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-green-600">
                              {entry.credit_usd > 0 ? `$${fmt(entry.credit_usd)}` : ''}
                            </td>
                            <td className={`px-4 py-3 text-right font-bold ${entry.balance_usd > 0 ? 'text-red-700' : 'text-green-700'}`}>
                              ${fmt(Math.abs(entry.balance_usd))}
                              {entry.balance_usd < 0 && <span className="text-xs font-normal ml-1">(CR)</span>}
                            </td>
                          </tr>
                          {isRowOpen && hasDetails && (
                            <tr className="bg-gray-50 border-t border-gray-100">
                              <td></td>
                              <td colSpan={5} className="px-4 py-3 text-xs text-gray-500">
                                {entry.details && <div className="mb-2">{entry.details}</div>}

                                {entry.entry_type === 'arrival' && (
                                  <div>
                                    {entry.items && entry.items.length > 0 && (
                                      <table className="w-full text-xs mb-3 bg-white border border-gray-200 rounded-lg overflow-hidden">
                                        <thead>
                                          <tr className="bg-gray-100 text-gray-500 uppercase">
                                            <th className="text-left px-3 py-1.5 font-semibold">Code</th>
                                            <th className="text-left px-3 py-1.5 font-semibold">Vendor Code</th>
                                            <th className="text-left px-3 py-1.5 font-semibold">Status</th>
                                            <th className="text-right px-3 py-1.5 font-semibold">Value (USD)</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                          {entry.items.map((it, i) => (
                                            <tr key={i} className="text-gray-700">
                                              <td className="px-3 py-1.5 font-mono font-semibold">{it.code}</td>
                                              <td className="px-3 py-1.5 text-gray-500">{it.vendor_item_code && it.vendor_item_code !== it.code ? it.vendor_item_code : '—'}</td>
                                              <td className="px-3 py-1.5">
                                                <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium
                                                  ${it.status === 'In Stock' ? 'bg-green-100 text-green-700' :
                                                    it.status === 'Sold' ? 'bg-gray-100 text-gray-500' :
                                                    'bg-amber-100 text-amber-700'}`}>
                                                  {it.status}
                                                </span>
                                              </td>
                                              <td className="px-3 py-1.5 text-right font-semibold">
                                                ${fmt(it.total_usd)}
                                                {it.is_fixed_price && <span className="ml-1 px-1 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-600">Fixed</span>}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    )}
                                    <button
                                      onClick={() => { setShowChargeForm(showChargeForm === idx ? null : idx); setChargeMsg(''); }}
                                      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-lg bg-orange-100 text-orange-700 hover:bg-orange-200 transition"
                                    >
                                      <Plus size={11} /> Add Charge
                                    </button>
                                    {showChargeForm === idx && (
                                      <div className="bg-white border border-orange-200 rounded-lg p-3 mt-2">
                                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-2">
                                          <div>
                                            <label className="block text-xs font-semibold text-gray-500 mb-1">Type *</label>
                                            <select value={chargeType} onChange={e => setChargeType(e.target.value)}
                                              className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-orange-400">
                                              {CHARGE_TYPES.map(t => <option key={t}>{t}</option>)}
                                            </select>
                                          </div>
                                          <div>
                                            <label className="block text-xs font-semibold text-gray-500 mb-1">Note</label>
                                            <input value={chargeDesc} onChange={e => setChargeDesc(e.target.value)} placeholder="Optional"
                                              className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-orange-400" />
                                          </div>
                                          <div>
                                            <label className="block text-xs font-semibold text-gray-500 mb-1">Amount ₹ INR</label>
                                            <input type="number" value={chargeAmtINR} onChange={e => setChargeAmtINR(e.target.value)} placeholder="0"
                                              className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-orange-400" />
                                          </div>
                                          <div>
                                            <label className="block text-xs font-semibold text-gray-500 mb-1">Amount $ USD</label>
                                            <input type="number" value={chargeAmtUSD} onChange={e => setChargeAmtUSD(e.target.value)} placeholder="0"
                                              className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-orange-400" />
                                          </div>
                                          <div className="flex items-end">
                                            <button onClick={() => handleAddCharge(entry)} disabled={chargeSubmitting}
                                              className="w-full px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold rounded transition disabled:opacity-50">
                                              {chargeSubmitting ? '…' : 'Save'}
                                            </button>
                                          </div>
                                        </div>
                                        {chargeMsg && <p className="text-xs text-red-600">{chargeMsg}</p>}
                                      </div>
                                    )}
                                  </div>
                                )}

                                {entry.entry_type === 'charge' && canDelete && (
                                  <button onClick={() => handleDeleteCharge(entry.id)} className="inline-flex items-center gap-1 text-red-500 hover:text-red-700">
                                    <Trash2 size={12} /> Remove charge
                                  </button>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  {ledger.ledger_entries.length > 0 && (
                    <tfoot className="border-t-2 border-gray-300 bg-gray-100">
                      <tr>
                        <td colSpan={3} className="px-4 py-3 font-bold text-gray-700 text-right">Closing Balance</td>
                        <td className="px-4 py-3 text-right font-bold text-red-700">${fmt(ledger.summary.total_usd_owed)}</td>
                        <td className="px-4 py-3 text-right font-bold text-green-700">${fmt(ledger.summary.total_paid_usd)}</td>
                        <td className={`px-4 py-3 text-right font-bold text-lg ${ledger.summary.outstanding_usd > 0 ? 'text-red-700' : 'text-green-700'}`}>
                          ${fmt(Math.abs(ledger.summary.outstanding_usd))}
                          {ledger.summary.outstanding_usd <= 0 && <span className="text-xs font-normal ml-1">(CR)</span>}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
              )}
            </div>

          </>
        )}
      </div>
    </div>
  );
};

export default VendorLedger;
